import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { STREAMING_DAY_OPTIONS, StreamingDays } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../../common/types';
import { isStreamingPeriodLocked } from '../streamingPeriod';
import { streamingMessages as messages } from '../streamingMessages';
import { StreamingUpgradePrompt } from './StreamingUpgradePrompt';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StreamingPeriodPickerProps {
  days: StreamingDays;
  /** The history the plan shows. A longer period shows a lock. */
  historyDaysLimit: number;
  onChange: (days: StreamingDays) => void;
}

/**
 * The period picker: 7d, 28d, 90d, 180d, 365d (design f6). A period past the plan history is not
 * disabled: a click opens the upgrade prompt below the picker.
 */
const StreamingPeriodPickerComponent: React.FC<StreamingPeriodPickerProps> = ({ days, historyDaysLimit, onChange }) => {
  const intl = useIntl();
  const [lockedDays, setLockedDays] = useState<StreamingDays>();
  const closePrompt = useCallback(() => setLockedDays(undefined), []);

  const options = useMemo(
    () =>
      STREAMING_DAY_OPTIONS.map((option) => {
        const isLocked = isStreamingPeriodLocked(option, historyDaysLimit);
        const pick = () => {
          if (isLocked) {
            // A second click on the same locked period closes the prompt.
            setLockedDays((current) => (current === option ? undefined : option));
            return;
          }
          setLockedDays(undefined);
          onChange(option);
        };
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={option === days}
            data-locked={isLocked ? 'true' : undefined}
            className={clsx(option === days && styles.periodOn, isLocked && styles.periodLocked)}
            onClick={pick}
          >
            {isLocked ? <FontAwesomeIcon icon={faLock} /> : null}
            {intl.formatMessage(messages.periodDays, { days: option })}
          </button>
        );
      }),
    [days, historyDaysLimit, intl, onChange]
  );

  return (
    <div className={styles.periodWrap}>
      <div className={styles.period} role="radiogroup" aria-label={intl.formatMessage(messages.periodLabel)}>
        {options}
      </div>
      {lockedDays === undefined ? null : <StreamingUpgradePrompt days={lockedDays} onClose={closePrompt} />}
    </div>
  );
};

export const StreamingPeriodPicker = TypedMemo(StreamingPeriodPickerComponent);
